/**
 * ItemCheckoutPage — Shopify-style checkout for marketplace items.
 *
 * Three-step flow:
 *   1. Review cart (readonly)
 *   2. Enter shipping address + email → fetch shipping rates → pick one
 *   3. Create PaymentIntent → render Stripe <PaymentElement/> → confirm
 *
 * After `payment_intent.succeeded` the Stripe webhook finalizes the
 * order server-side. The FE polls `/public/item-checkout/order/{id}`
 * for ~15s and shows a confirmation on `status=paid`.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { CheckCircle2, ChevronLeft, Loader2, Lock, Package, Store, Truck } from 'lucide-react';
import { toast } from 'sonner';

import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import useSeo from '@/hooks/useSeo';
import { useAuth } from '@/context/AuthContext';
import { useMarketplaceCart } from '@/context/MarketplaceCartContext';
import TurnstileWidget from '@/components/TurnstileWidget';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const stripePromiseCache = {};
function getStripePromise(pub) {
  if (!pub) return null;
  if (!stripePromiseCache[pub]) stripePromiseCache[pub] = loadStripe(pub);
  return stripePromiseCache[pub];
}

function abs(url) {
  if (!url) return '';
  return url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url;
}

/* ──────────────────────── Payment form ───────────────────────── */
function PayForm({ orderId, totalCents }) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');

  const submit = useCallback(async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setSubmitting(true);
    setErr('');
    const returnUrl = `${window.location.origin}/shop/checkout?status=success&order_id=${orderId}`;
    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: returnUrl },
      redirect: 'if_required',
    });
    if (error) {
      setErr(error.message || 'Payment failed. Try a different card.');
      setSubmitting(false);
      return;
    }
    window.location.assign(returnUrl);
  }, [stripe, elements, orderId]);

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="item-checkout-pay-form">
      <div className="p-5 rounded-xl bg-white border border-white/10">
        <PaymentElement options={{ layout: 'tabs' }} />
      </div>
      {err && <p className="text-xs text-red-300" data-testid="item-checkout-err">{err}</p>}
      <button
        type="submit"
        disabled={!stripe || submitting}
        className="w-full py-4 rounded-full text-sm font-bold uppercase tracking-wider text-white disabled:opacity-40 inline-flex items-center justify-center gap-2 transition-all hover:shadow-[0_10px_40px_rgba(167,139,250,0.5)] bg-gradient-to-r from-[#8B5CF6] via-[#A78BFA] to-[#EC4899]"
        data-testid="item-checkout-pay"
      >
        <Lock size={13} /> {submitting ? 'Processing…' : `Pay $${(totalCents / 100).toFixed(2)}`}
      </button>
      <p className="text-[10px] text-white/40 text-center tracking-wide">
        Secured by Stripe · Zero platform fees · 100% of the sale goes to the artisan.
      </p>
    </form>
  );
}

/* ──────────────────────── Page ───────────────────────── */
export default function ItemCheckoutPage() {
  useSeo({ title: 'Checkout · EntrepreNatives', description: 'Secure Stripe checkout for marketplace items.' });
  const navigate = useNavigate();
  const { user } = useAuth();
  const [params] = useSearchParams();
  const successOrderId = params.get('status') === 'success' ? params.get('order_id') : null;

  const {
    cart, subtotal, buyerEmail, setBuyerEmail,
    shippingAddress, setShippingAddress, clearCart,
  } = useMarketplaceCart();

  const [buyerName, setBuyerName] = useState(user?.name || '');
  const [rates, setRates] = useState([]);
  const [chosenRate, setChosenRate] = useState(null);
  const [loadingRates, setLoadingRates] = useState(false);
  const [intent, setIntent] = useState(null);  // { client_secret, order_id, publishable_key, amount_cents }
  const [creatingIntent, setCreatingIntent] = useState(false);
  const [paidOrder, setPaidOrder] = useState(null);
  // Cloudflare Turnstile token — required by backend anti-abuse guard.
  const [turnstileToken, setTurnstileToken] = useState('');

  // Keep buyerEmail in sync with authenticated user if present.
  useEffect(() => {
    if (user?.email && !buyerEmail) setBuyerEmail(user.email);
  }, [user, buyerEmail, setBuyerEmail]);

  // Shipping total
  const shippingTotal = chosenRate ? Number(chosenRate.amount || 0) : 0;
  const totalCents = useMemo(
    () => Math.round(subtotal * 100) + Math.round(shippingTotal * 100),
    [subtotal, shippingTotal],
  );

  /* ── Success state (post-redirect from Stripe) ────────────── */
  useEffect(() => {
    if (!successOrderId) return;
    let cancelled = false;
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await axios.get(`${API}/public/item-checkout/order/${successOrderId}`);
        if (!cancelled && data?.status === 'paid') {
          setPaidOrder(data);
          clearCart();
          return;
        }
      } catch {}
      if (!cancelled && attempts < 15) setTimeout(poll, 1000);
      else if (!cancelled && !paidOrder) {
        // Fallback: fetch once more to show the pending state.
        try {
          const { data } = await axios.get(`${API}/public/item-checkout/order/${successOrderId}`);
          setPaidOrder(data);
        } catch {}
      }
    };
    poll();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [successOrderId]);

  /* ── Fetch shipping rates when the shopper has a complete address ── */
  const canQuote = useMemo(() => (
    buyerEmail.trim() && shippingAddress.line1 && shippingAddress.city &&
    shippingAddress.state && shippingAddress.zip && shippingAddress.country && cart.items.length > 0
  ), [buyerEmail, shippingAddress, cart.items.length]);

  const fetchRates = useCallback(async () => {
    if (!canQuote) return;
    setLoadingRates(true);
    try {
      const { data } = await axios.post(`${API}/public/item-checkout/quote-shipping`, {
        items: cart.items.map((l) => ({ item_id: l.item_id, variant_id: l.variant_id, qty: l.quantity })),
        destination: {
          country: shippingAddress.country,
          zip: shippingAddress.zip,
          state: shippingAddress.state,
          city: shippingAddress.city,
          line1: shippingAddress.line1,
          line2: shippingAddress.line2,
        },
      });
      setRates(data.rates || []);
      setChosenRate(data.rates?.[0] || null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not fetch shipping rates');
      setRates([]);
      setChosenRate(null);
    } finally {
      setLoadingRates(false);
    }
  }, [canQuote, cart.items, shippingAddress]);

  const createIntent = useCallback(async () => {
    if (!chosenRate) {
      toast.error('Pick a shipping option first');
      return;
    }
    if (!turnstileToken) {
      toast.error('Please complete the security check');
      return;
    }
    setCreatingIntent(true);
    try {
      const headers = {};
      const token = localStorage.getItem('token');
      if (token) headers.Authorization = `Bearer ${token}`;
      const { data } = await axios.post(
        `${API}/public/item-checkout/intent`,
        {
          items: cart.items.map((l) => ({ item_id: l.item_id, variant_id: l.variant_id, qty: l.quantity })),
          buyer_email: buyerEmail,
          buyer_name: buyerName,
          shipping_address: shippingAddress,
          shipping_rate: chosenRate,
          turnstile_token: turnstileToken,
        },
        { headers },
      );
      setIntent(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not create payment intent');
    } finally {
      setCreatingIntent(false);
    }
  }, [chosenRate, cart.items, buyerEmail, buyerName, shippingAddress, turnstileToken]);

  /* ── Render: Success ───────────────────────────────────────── */
  if (paidOrder) {
    return (
      <div className="min-h-screen bg-[#0A0A23] text-white">
        <Navbar />
        <main className="pt-28 pb-24">
          <div className="container-main max-w-xl text-center">
            <div className="w-16 h-16 mx-auto rounded-full bg-emerald-500/20 flex items-center justify-center mb-6">
              <CheckCircle2 className="w-9 h-9 text-emerald-300" strokeWidth={1.5} />
            </div>
            <h1 className="font-syne text-3xl font-bold" data-testid="item-checkout-success-title">
              {paidOrder.status === 'paid' ? 'Payment confirmed!' : 'Processing payment…'}
            </h1>
            <p className="mt-3 text-[#9CA3AF]">
              {paidOrder.status === 'paid'
                ? `Thanks ${paidOrder.buyer_name || ''}, your order #${paidOrder.order_id} is on the way.`
                : 'Your payment is processing. We will email you a receipt as soon as it clears.'}
            </p>
            <div className="mt-8 flex items-center justify-center gap-3">
              <Link
                to="/shop"
                className="px-5 py-3 rounded-full text-sm font-semibold bg-white/5 border border-white/10 hover:bg-white/10"
                data-testid="item-checkout-back-shop"
              >
                Keep shopping
              </Link>
              {user && (
                <Link
                  to="/dashboard/consumer"
                  className="px-5 py-3 rounded-full text-sm font-semibold bg-white text-[#0E0E23] hover:bg-[#F4F4F7]"
                >
                  View my orders
                </Link>
              )}
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  /* ── Render: Empty cart ────────────────────────────────────── */
  if (!cart.items.length) {
    return (
      <div className="min-h-screen bg-[#0A0A23] text-white">
        <Navbar />
        <main className="pt-28 pb-24 text-center">
          <div className="container-main max-w-md">
            <Package size={48} className="mx-auto text-[#9CA3AF]" strokeWidth={1} />
            <h1 className="mt-6 font-syne text-3xl font-bold">Your cart is empty</h1>
            <p className="mt-2 text-[#9CA3AF] text-sm">Pick a few handcrafted finds from the Shop and come back here.</p>
            <Link
              to="/shop"
              className="mt-6 inline-flex items-center gap-2 px-5 py-3 rounded-full font-semibold text-sm bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6]"
            >
              Browse the Shop
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const addressField = (name, placeholder, span = 'col-span-2', opts = {}) => (
    <div className={span}>
      <input
        value={shippingAddress[name] || ''}
        onChange={(e) => setShippingAddress({ ...shippingAddress, [name]: e.target.value })}
        placeholder={placeholder}
        className="w-full h-11 px-3.5 rounded-lg bg-white/5 border border-white/10 text-sm text-white placeholder:text-[#9CA3AF] focus:outline-none focus:border-[#8B5CF6]"
        data-testid={`item-checkout-addr-${name}`}
        {...opts}
      />
    </div>
  );

  /* ── Render: Checkout ──────────────────────────────────────── */
  return (
    <div className="min-h-screen bg-[#0A0A23] text-white">
      <Navbar />
      <main className="pt-24 pb-20">
        <div className="container-main max-w-6xl">
          <Link to="/shop" className="inline-flex items-center gap-1.5 text-sm text-[#9CA3AF] hover:text-[#C4B5FD] mb-6">
            <ChevronLeft size={14} /> Keep shopping
          </Link>

          <div className="grid lg:grid-cols-[1fr_380px] gap-10">
            {/* ─── Left: forms ───────────────────────────────── */}
            <div className="space-y-10">
              {/* Contact */}
              <section>
                <h2 className="font-syne text-lg font-bold mb-4">Contact</h2>
                <div className="grid grid-cols-2 gap-3">
                  <input
                    type="email"
                    value={buyerEmail}
                    onChange={(e) => setBuyerEmail(e.target.value)}
                    placeholder="Email"
                    className="col-span-2 h-11 px-3.5 rounded-lg bg-white/5 border border-white/10 text-sm text-white placeholder:text-[#9CA3AF] focus:outline-none focus:border-[#8B5CF6]"
                    data-testid="item-checkout-email"
                  />
                  <input
                    value={buyerName}
                    onChange={(e) => setBuyerName(e.target.value)}
                    placeholder="Full name"
                    className="col-span-2 h-11 px-3.5 rounded-lg bg-white/5 border border-white/10 text-sm text-white placeholder:text-[#9CA3AF] focus:outline-none focus:border-[#8B5CF6]"
                    data-testid="item-checkout-name"
                  />
                </div>
              </section>

              {/* Address */}
              <section>
                <h2 className="font-syne text-lg font-bold mb-4">Shipping address</h2>
                <div className="grid grid-cols-2 gap-3">
                  {addressField('line1', 'Street address')}
                  {addressField('line2', 'Apt / Suite (optional)')}
                  {addressField('city', 'City', 'col-span-1')}
                  {addressField('state', 'State / Province', 'col-span-1')}
                  {addressField('zip', 'ZIP / Postal code', 'col-span-1')}
                  {addressField('country', 'Country (e.g. US)', 'col-span-1')}
                </div>
                <button
                  onClick={fetchRates}
                  disabled={!canQuote || loadingRates}
                  className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold bg-white/5 border border-white/10 hover:bg-white/10 disabled:opacity-40"
                  data-testid="item-checkout-get-rates"
                >
                  {loadingRates ? <Loader2 size={13} className="animate-spin" /> : <Truck size={13} />}
                  Get shipping rates
                </button>
              </section>

              {/* Rates */}
              {rates.length > 0 && (
                <section>
                  <h2 className="font-syne text-lg font-bold mb-4">Choose a shipping option</h2>
                  <div className="space-y-2.5" data-testid="item-checkout-rates">
                    {rates.map((r, i) => {
                      const selected = chosenRate?.id === r.id && chosenRate?.carrier === r.carrier;
                      const amt = Number(r.amount || 0).toFixed(2);
                      return (
                        <label
                          key={`${r.id}-${i}`}
                          className={`flex items-center gap-3 p-3.5 rounded-lg border cursor-pointer transition-all ${selected ? 'bg-[#8B5CF6]/15 border-[#8B5CF6]' : 'bg-white/5 border-white/10 hover:border-white/30'}`}
                          data-testid={`item-checkout-rate-${i}`}
                        >
                          <input
                            type="radio"
                            name="rate"
                            checked={selected}
                            onChange={() => setChosenRate(r)}
                            className="accent-[#8B5CF6]"
                          />
                          <div className="flex-1">
                            <p className="text-sm font-semibold text-white">{r.carrier?.toUpperCase() || 'STD'} · {r.service || r.label}</p>
                            {(r.min_days != null && r.max_days != null) && (
                              <p className="text-xs text-[#9CA3AF]">{r.min_days}–{r.max_days} business days</p>
                            )}
                          </div>
                          <span className="font-semibold text-white">{Number(amt) === 0 ? 'Free' : `$${amt}`}</span>
                        </label>
                      );
                    })}
                  </div>
                </section>
              )}

              {/* Payment */}
              {intent ? (
                <section>
                  <h2 className="font-syne text-lg font-bold mb-4">Payment</h2>
                  <Elements
                    stripe={getStripePromise(intent.publishable_key)}
                    options={{ clientSecret: intent.client_secret, appearance: { theme: 'stripe' } }}
                  >
                    <PayForm orderId={intent.order_id} totalCents={intent.amount_cents} />
                  </Elements>
                </section>
              ) : (
                <section className="space-y-4">
                  {/* Cloudflare Turnstile — invisible unless the shopper
                      looks sus. Required by the backend anti-abuse guard. */}
                  <TurnstileWidget onToken={setTurnstileToken} action="item_checkout" />
                  <button
                    onClick={createIntent}
                    disabled={!chosenRate || creatingIntent || !turnstileToken}
                    className="w-full py-4 rounded-full text-sm font-bold uppercase tracking-wider text-white disabled:opacity-40 inline-flex items-center justify-center gap-2 transition-all bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] hover:shadow-[0_10px_40px_rgba(139,92,246,0.5)]"
                    data-testid="item-checkout-continue"
                  >
                    {creatingIntent ? 'Preparing…' : 'Continue to payment'}
                  </button>
                </section>
              )}
            </div>

            {/* ─── Right: order summary ───────────────────────── */}
            <aside className="lg:sticky lg:top-24 self-start">
              <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xs uppercase tracking-wider text-[#9CA3AF] font-semibold">Order summary</h3>
                  {cart.vendor_name && (
                    <span className="inline-flex items-center gap-1 text-[11px] text-[#C4B5FD]">
                      <Store size={11} /> {cart.vendor_name}
                    </span>
                  )}
                </div>

                <div className="space-y-3 max-h-80 overflow-y-auto pr-1" data-testid="item-checkout-summary">
                  {cart.items.map((l) => (
                    <div key={`${l.item_id}:${l.variant_id || ''}`} className="flex gap-3">
                      <div className="w-14 h-14 rounded-lg overflow-hidden bg-[#1A1D4A] flex-shrink-0 border border-white/5">
                        {l.image ? <img src={abs(l.image)} alt="" className="w-full h-full object-cover" /> : <Package className="w-full h-full p-3 text-[#9CA3AF]" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-white truncate">{l.title}</p>
                        {l.variant_label && <p className="text-xs text-[#9CA3AF]">{l.variant_label}</p>}
                        <p className="text-xs text-[#9CA3AF]">Qty {l.quantity}</p>
                      </div>
                      <p className="text-sm font-semibold text-white">${(l.unit_price * l.quantity).toFixed(2)}</p>
                    </div>
                  ))}
                </div>

                <div className="mt-5 pt-5 border-t border-white/10 space-y-2 text-sm">
                  <div className="flex justify-between text-[#9CA3AF]">
                    <span>Subtotal</span>
                    <span className="text-white">${subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-[#9CA3AF]">
                    <span>Shipping</span>
                    <span className="text-white">{chosenRate ? (shippingTotal === 0 ? 'Free' : `$${shippingTotal.toFixed(2)}`) : '—'}</span>
                  </div>
                  <div className="flex justify-between items-baseline mt-3 pt-3 border-t border-white/10">
                    <span className="text-xs uppercase tracking-wider text-[#9CA3AF] font-semibold">Total</span>
                    <span className="font-syne text-xl font-bold text-white">${(totalCents / 100).toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
